import { Mic } from '@/assets/icons';
import { StopCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/base/Button';
import { formatDuration } from '@/lib/types/consultation';
import type { VoiceNoteState } from '@/lib/hooks/useVoiceNote';

interface SmartVoiceNoteProps {
  state: VoiceNoteState;
  recordingDuration: number;
  onStart: () => void;
  onStop: () => void;
  disabled?: boolean;
}

export function SmartVoiceNote({
  state,
  recordingDuration,
  onStart,
  onStop,
  disabled,
}: SmartVoiceNoteProps) {
  if (state === 'processing') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled
        className="border-[#f8f9fb] bg-[#fffceb] text-[#526ec5]"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Processing...
      </Button>
    );
  }

  if (state === 'recording') {
    return (
      <div className="flex h-9 items-center gap-1 rounded-lg border border-[#f8f9fb] pl-1">
        {/* Mic icon */}
        <Mic className="h-5 w-5 shrink-0 text-[#243056]" />

        {/* Waveform visualization */}
        <div className="flex h-full items-center gap-[2px] px-1">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="w-[1.5px] animate-pulse rounded-full bg-[#243056]"
              style={{
                height: `${8 + Math.sin(i * 0.8 + recordingDuration) * 10}px`,
                animationDelay: `${i * 80}ms`,
                animationDuration: '0.6s',
              }}
            />
          ))}
        </div>

        {/* Duration */}
        <span className="shrink-0 px-1 text-[10px] font-medium text-[#717072]">
          {formatDuration(recordingDuration)}
        </span>

        {/* Stop recording button */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onStop}
          className="ml-1 border-[#f8f9fb] bg-[#fefeff] text-[#e85c5c] hover:bg-[#fff0f0]"
        >
          <StopCircle className="h-4 w-4" />
          Stop recording
        </Button>
      </div>
    );
  }

  // Idle state — "Add voice note" button
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onStart}
      disabled={disabled}
      className="border-[#f8f9fb] bg-[#fffceb] text-[#526ec5] hover:bg-[#fff8d6]"
    >
      <Mic className="h-4 w-4" />
      Add voice note
    </Button>
  );
}
